import React, { useState, useEffect, useCallback } from "react";
import { QrCode, Shield, Package, MapPin, Calendar, CheckCircle, XCircle, AlertTriangle, Zap, RefreshCw, Scan, Search, Eye, Lock, Unlock, History, Filter, X } from 'lucide-react';

// Types
interface Product { productId: string; _id?: string; name: string; sku: string; description?: string; businessId?: string; }
interface Batch { batchId: string; name: string; assignedStoreId?: string; productId?: string; batchNumber?: string; status?: string; }
interface Store { storeId: string; storeName: string; location?: string; address?: string; isActive?: boolean; }
interface Tag {
  tagId: string; status: string; businessId?: string; storeId?: string; productId?: string; batchId?: string;
  activation?: { activatedAt?: string; activatedBy?: string; method?: string; };
  stats?: { totalScans?: number; lastScannedAt?: string; };
  statusHistory?: Array<{ status: string; timestamp: string; changedBy?: string; reason?: string; }>;
}
interface Library { libraryId: string; name: string; description?: string; tagCount?: number; }

// ═══════════════════════════════════════════════════════════
// API Service — auto-detects base URL:
//   Production: https://api.jeptags.com/api
//   Localhost:   /api  (relative, works with proxy)
//
// Endpoint paths (no /api prefix — base URL already has it):
//   /products, /stores, /batches, /tags
// ═══════════════════════════════════════════════════════════
class TagsApiService {
  private static getApiBase(): string {
    // Check for env variable first (Create React App or Vite)
    const envUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
      || (typeof process !== 'undefined' && process.env?.VITE_API_URL)
      || '';
    if (envUrl) return envUrl;

    // Auto-detect from hostname
    const host = window.location.hostname;
    if (host === 'jeptags.com' || host === 'www.jeptags.com' || host.endsWith('.jeptags.com')) {
      return 'https://api.jeptags.com/api';
    }
    // Localhost — use relative URL (works with proxy or same-origin)
    return '/api';
  }

  private static API = TagsApiService.getApiBase();

  private static getToken() {
    return localStorage.getItem('businessToken') || localStorage.getItem('auth_token') || localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken') || localStorage.getItem('jwtToken') || '';
  }

  private static getCurrentBusinessId() {
    const d = localStorage.getItem('businessData');
    if (d) {
      try {
        const p = JSON.parse(d);
        // businessId is always a string like 'BIZ28659149'
        // p._id may be an ObjectId object — never use it directly in URLs
        const bid = p.businessId || p.business_id || '';
        if (bid && typeof bid === 'string') return bid;
      } catch { /* */ }
    }
    return localStorage.getItem('businessId') || '';
  }

  private static authHeaders(): Record<string, string> {
    return { 'Authorization': `Bearer ${this.getToken()}`, 'Content-Type': 'application/json' };
  }

  // Guard against HTML responses and handle auth errors
  private static async safeFetch(url: string): Promise<any> {
    const token = this.getToken();
    if (!token) {
      console.warn(`[TagsAPI] No auth token found in localStorage. Checked: businessToken, auth_token, authToken, token`);
    }
    const res = await fetch(url, { headers: this.authHeaders() });
    // Handle auth errors — don't retry
    if (res.status === 401 || res.status === 403) {
      console.error(`[TagsAPI] Auth error ${res.status} on ${url}. Token present: ${!!token}, token key: ${this.getTokenKey()}`);
      return null;
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      console.warn(`[TagsAPI] Non-JSON from ${url} — is this route registered?`);
      return null;
    }
    return res.json();
  }

  // Helper: which localStorage key has the token
  private static getTokenKey(): string {
    if (localStorage.getItem('businessToken')) return 'businessToken';
    if (localStorage.getItem('auth_token')) return 'auth_token';
    if (localStorage.getItem('authToken')) return 'authToken';
    if (localStorage.getItem('token')) return 'token';
    if (localStorage.getItem('accessToken')) return 'accessToken';
    if (localStorage.getItem('jwtToken')) return 'jwtToken';
    return 'none';
  }

  // GET /api/products?businessId=...
  static async getProducts(): Promise<Product[]> {
    try {
      const json = await this.safeFetch(`${this.API}/products?businessId=${this.getCurrentBusinessId()}`);
      if (!json) return [];
      const list = json?.data?.products || json?.products || [];
      return list.map((p: any) => ({
        productId: p.productId || p._id, _id: p._id, name: p.name || 'Unnamed Product',
        sku: p.sku || 'NO-SKU', description: p.description, businessId: p.businessId
      }));
    } catch (e) { console.error('Error loading products:', e); return []; }
  }

  // GET /api/batches?businessId=...&productId=...
  static async getBatches(productId?: string): Promise<Batch[]> {
    try {
      let url = `${this.API}/batches?businessId=${this.getCurrentBusinessId()}`;
      // ✅ Filter by productId on server side
      if (productId && productId.trim()) {
        url += `&productId=${encodeURIComponent(productId.trim())}`;
      }
      const json = await this.safeFetch(url);
      if (!json) return [];
      const list = json?.data?.batches || json?.batches || json?.data || [];
      return list.map((b: any) => {
        // ✅ Normalize productId (handles ObjectId objects)
        const pid = b.productId?._id || b.productId?.productId || b.productId || '';
        return {
          batchId: b.batchId || b._id, name: b.name || b.batchNumber || 'Unnamed Batch',
          assignedStoreId: b.assignedStoreId,
          productId: typeof pid === 'object' ? String(pid) : pid,
          batchNumber: b.batchNumber, status: b.status
        };
      });
    } catch (e) { console.error('Error loading batches:', e); return []; }
  }

  // GET /api/stores?businessId=...
  static async getStores(): Promise<Store[]> {
    try {
      const json = await this.safeFetch(`${this.API}/stores?businessId=${this.getCurrentBusinessId()}`);
      if (!json) return [];
      const list = json?.data?.stores || json?.stores || [];
      return list.map((s: any) => ({
        storeId: s.storeId || s._id, storeName: s.storeName || s.name,
        location: typeof s.location === 'string' ? s.location : (s.address?.formatted || ''),
        address: typeof s.address === 'string' ? s.address : (s.address?.formatted || ''),
        isActive: s.isActive !== false
      }));
    } catch (e) { console.error('Error loading stores:', e); return []; }
  }

  // GET /api/tags/search-optimized?businessId=...&batchId=...
  static async getTags(batchId?: string): Promise<Tag[]> {
    try {
      let url = `${this.API}/tags/search-optimized?businessId=${this.getCurrentBusinessId()}&limit=100`;
      if (batchId) url += `&batchId=${batchId}`;
      const json = await this.safeFetch(url);
      return json?.tags || [];
    } catch (e) { console.error('Error loading tags:', e); return []; }
  }

  // GET /api/tags/:tagId
  static async getTagDetails(tagId: string): Promise<Tag | null> {
    try {
      const json = await this.safeFetch(`${this.API}/tags/${tagId}`);
      return json?.tag || null;
    } catch (e) { console.error('Error loading tag details:', e); return null; }
  }

  // POST /api/tags/business/scan-activate/:tagId
  static async activateTag(tagId: string, data: {
    businessId: string; storeId: string; productId: string; batchId: string; activatedBy?: string; location?: string;
  }) {
    const res = await fetch(`${this.API}/tags/business/scan-activate/${tagId}`, {
      method: 'POST', headers: this.authHeaders(), body: JSON.stringify(data)
    });
    return res.json();
  }

  // POST /api/tags/:tagId/block
  static async blockTag(tagId: string, reason?: string) {
    const res = await fetch(`${this.API}/tags/${tagId}/block`, {
      method: 'POST', headers: this.authHeaders(), body: JSON.stringify({ reason })
    });
    return res.json();
  }

  // POST /api/tags/:tagId/unblock
  static async unblockTag(tagId: string) {
    const res = await fetch(`${this.API}/tags/${tagId}/unblock`, {
      method: 'POST', headers: this.authHeaders()
    });
    return res.json();
  }

  // POST /api/tags/business/activate-csv-simple
  static async activateCSV(csvData: string, data: {
    businessId: string; storeId: string; productId: string; batchId: string; activatedBy?: string;
  }) {
    const token = this.getToken();
    const fd = new FormData();
    fd.append('file', new Blob([csvData], { type: 'text/csv' }), 'tags.csv');
    fd.append('businessId', data.businessId);
    fd.append('storeId', data.storeId);
    fd.append('productId', data.productId);
    fd.append('batchId', data.batchId);
    if (data.activatedBy) fd.append('activatedBy', data.activatedBy);
    const res = await fetch(`${this.API}/tags/business/activate-csv-simple`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
    });
    return res.json();
  }

  // GET /api/tags/libraries
  static async getLibraries(): Promise<Library[]> {
    try {
      const json = await this.safeFetch(`${this.API}/tags/libraries`);
      return json?.libraries || [];
    } catch (e) { console.error('Error loading libraries:', e); return []; }
  }

  // GET /api/tags/business/activation-stats
  static async getActivationStats(params: { businessId: string; storeId?: string; productId?: string; batchId?: string; }) {
    try {
      const qp = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')));
      const json = await this.safeFetch(`${this.API}/tags/business/activation-stats?${qp}`);
      return json || { success: false, error: 'No response' };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

// ═══════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════
const TagsManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [singleTagId, setSingleTagId] = useState("");
  const [csvData, setCsvData] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [operationLoading, setOperationLoading] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [showTagDetails, setShowTagDetails] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  const businessId = (() => {
    const d = localStorage.getItem('businessData');
    if (d) { try { const p = JSON.parse(d); const bid = p.businessId || p.business_id || ''; return typeof bid === 'string' ? bid : ''; } catch { return ''; } }
    return localStorage.getItem('businessId') || '';
  })();

  const showMsg = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setMessage(msg); setMessageType(type); setTimeout(() => setMessage(""), 5000);
  }, []);

  const filteredTags = tags.filter(tag => {
    const s = tag.tagId.toLowerCase().includes(searchQuery.toLowerCase());
    const st = statusFilter === "all" || tag.status === statusFilter;
    return s && st;
  });

  const loadBatches = useCallback(async (productId?: string) => {
    try { 
      // ✅ Pass productId to filter on server
      setBatches(await TagsApiService.getBatches(productId)); 
    } catch { /* */ }
  }, []);

  const loadTags = useCallback(async (batchId: string) => {
    try { setTags(await TagsApiService.getTags(batchId)); } catch { setTags([]); }
  }, []);

  const loadTagDetails = useCallback(async (tagId: string) => {
    try {
      const d = await TagsApiService.getTagDetails(tagId);
      if (d) { setSelectedTag(d); setShowTagDetails(true); }
    } catch { showMsg('Failed to load tag details', 'error'); }
  }, [showMsg]);

  const loadAllData = useCallback(async () => {
    if (!businessId) { showMsg('No business ID found. Please log in again.', 'error'); setLoading(false); return; }
    setLoading(true);
    try {
      const [p, s, l] = await Promise.all([TagsApiService.getProducts(), TagsApiService.getStores(), TagsApiService.getLibraries()]);
      setProducts(p); setStores(s); setLibraries(l);
      if (s.length === 1) setSelectedStoreId(s[0].storeId);
      await loadBatches();
      if (p.length === 0) showMsg('No products found. Create products first.', 'info');
      else showMsg(`Loaded ${p.length} products, ${s.length} stores`, 'success');
    } catch { showMsg('Failed to load data.', 'error'); }
    finally { setLoading(false); }
  }, [businessId, showMsg, loadBatches]);

  const loadStats = useCallback(async () => {
    if (!businessId) return;
    try {
      const r = await TagsApiService.getActivationStats({
        businessId, storeId: selectedStoreId || undefined, productId: selectedProductId || undefined, batchId: selectedBatchId || undefined
      });
      if (r.success) { setStats(r); setShowStats(true); } else showMsg('Failed to load stats', 'error');
    } catch { showMsg('Failed to load activation stats', 'error'); }
  }, [businessId, selectedStoreId, selectedProductId, selectedBatchId, showMsg]);

  useEffect(() => { loadAllData(); }, [loadAllData]);
  // Only reload batches when product selection changes — NOT when batches array changes
  useEffect(() => {
    if (selectedProductId) {
      setSelectedBatchId(""); // ✅ reset batch when product changes
      loadBatches(selectedProductId); // ✅ filter by product
    } else {
      setSelectedBatchId("");
      loadBatches(); // load all
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId]);
  useEffect(() => { if (selectedBatchId) loadTags(selectedBatchId); else setTags([]); }, [selectedBatchId, loadTags]);

  const handleActivateTag = async () => {
    if (!singleTagId.trim()) { showMsg('Please enter a tag ID', 'error'); return; }
    if (!selectedStoreId || !selectedProductId || !selectedBatchId) { showMsg('Please select store, product, and batch first', 'error'); return; }
    setOperationLoading(true);
    try {
      const r = await TagsApiService.activateTag(singleTagId.trim(), { businessId, storeId: selectedStoreId, productId: selectedProductId, batchId: selectedBatchId, activatedBy: 'manual-scan', location: 'Dashboard' });
      if (r.success) { showMsg(`Tag ${singleTagId} activated!`, 'success'); setSingleTagId(""); if (selectedBatchId) await loadTags(selectedBatchId); }
      else showMsg(r.error || 'Failed to activate tag', 'error');
    } catch (e: any) { showMsg(e.message || 'Activation failed', 'error'); }
    finally { setOperationLoading(false); }
  };

  const handleBlockTag = async (tagId: string) => {
    setOperationLoading(true);
    try {
      const r = await TagsApiService.blockTag(tagId, blockReason);
      if (r.success) { showMsg(`Tag ${tagId} blocked`, 'success'); setShowTagDetails(false); setBlockReason(""); if (selectedBatchId) await loadTags(selectedBatchId); }
      else showMsg(r.error || 'Failed', 'error');
    } catch (e: any) { showMsg(e.message || 'Failed', 'error'); }
    finally { setOperationLoading(false); }
  };

  const handleUnblockTag = async (tagId: string) => {
    setOperationLoading(true);
    try {
      const r = await TagsApiService.unblockTag(tagId);
      if (r.success) { showMsg(`Tag ${tagId} unblocked`, 'success'); setShowTagDetails(false); if (selectedBatchId) await loadTags(selectedBatchId); }
      else showMsg(r.error || 'Failed', 'error');
    } catch (e: any) { showMsg(e.message || 'Failed', 'error'); }
    finally { setOperationLoading(false); }
  };

  const handleCSVActivation = async () => {
    if (!csvData.trim()) { showMsg('Please enter CSV data', 'error'); return; }
    if (!selectedStoreId || !selectedProductId || !selectedBatchId) { showMsg('Please select store, product, and batch first', 'error'); return; }
    setOperationLoading(true);
    try {
      const r = await TagsApiService.activateCSV(csvData.trim(), { businessId, storeId: selectedStoreId, productId: selectedProductId, batchId: selectedBatchId, activatedBy: 'csv-upload' });
      if (r.success) { showMsg(`${r.summary?.tagsActivated || 0} tags activated from CSV!`, 'success'); setCsvData(""); if (selectedBatchId) await loadTags(selectedBatchId); }
      else showMsg(r.error || 'CSV activation failed', 'error');
    } catch (e: any) { showMsg(e.message || 'CSV activation failed', 'error'); }
    finally { setOperationLoading(false); }
  };

  const getStatusIcon = (s: string) => {
    switch (s) {
      case 'active': case 'activated': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending': case 'imported': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'blocked': case 'recalled': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };
  const getStatusColor = (s: string) => {
    switch (s) {
      case 'active': case 'activated': return 'bg-green-100 text-green-800';
      case 'pending': case 'imported': return 'bg-yellow-100 text-yellow-800';
      case 'blocked': case 'recalled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const selectedProduct = products.find(p => p.productId === selectedProductId);
  const selectedBatch = batches.find(b => b.batchId === selectedBatchId);
  const selectedStore = stores.find(s => s.storeId === selectedStoreId);
  // Show all batches — product/batch ID formats may differ (custom ID vs ObjectId)
  // If batches have productId, try to match; otherwise show all
  // ✅ Server already filters by productId — client filter as backup
  const availableBatches = selectedProductId
    ? batches.filter(b => {
        if (!b.productId) return false; // hide unassigned if product selected
        const bProd = String(b.productId);
        const sProd = String(selectedProductId);
        const sId   = String(selectedProduct?._id || '');
        return bProd === sProd || bProd === sId;
      })
    : batches;
  // ✅ Show filtered batches only — no fallback to ALL
  const displayBatches = availableBatches;
  const uniqueStatuses = Array.from(new Set(tags.map(t => t.status).filter(Boolean)));

  if (loading) {
    return (<div className="flex items-center justify-center min-h-64"><div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading tags manager...</p>
    </div></div>);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h2 className="text-3xl font-bold text-gray-900">Tags Manager</h2><p className="text-gray-600">Activate, search, and manage NFC, RFID, and QR tags</p></div>
        <div className="flex space-x-3">
          <button onClick={loadAllData} disabled={loading} className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"><RefreshCw className="h-4 w-4 mr-2" />Refresh</button>
          <button onClick={loadStats} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Zap className="h-4 w-4 mr-2" />View Stats</button>
        </div>
      </div>

      {message && (<div className={`p-4 rounded-lg border ${messageType === 'success' ? 'bg-green-50 border-green-200 text-green-800' : messageType === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>{message}</div>)}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border"><div className="flex items-center"><Package className="h-8 w-8 text-blue-600" /><div className="ml-3"><div className="text-2xl font-bold text-blue-600">{products.length}</div><div className="text-sm text-gray-600">Products</div></div></div></div>
        <div className="bg-white rounded-lg p-4 shadow-sm border"><div className="flex items-center"><MapPin className="h-8 w-8 text-green-600" /><div className="ml-3"><div className="text-2xl font-bold text-green-600">{stores.length}</div><div className="text-sm text-gray-600">Stores</div></div></div></div>
        <div className="bg-white rounded-lg p-4 shadow-sm border"><div className="flex items-center"><QrCode className="h-8 w-8 text-purple-600" /><div className="ml-3"><div className="text-2xl font-bold text-purple-600">{filteredTags.length}</div><div className="text-sm text-gray-600">Filtered Tags</div></div></div></div>
        <div className="bg-white rounded-lg p-4 shadow-sm border"><div className="flex items-center"><Shield className="h-8 w-8 text-orange-600" /><div className="ml-3"><div className="text-2xl font-bold text-orange-600">{libraries.length}</div><div className="text-sm text-gray-600">Libraries</div></div></div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><MapPin className="h-5 w-5 mr-2 text-blue-600" />1. Select Store</h3>
          <select value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Choose store...</option>
            {stores.map(s => <option key={s.storeId} value={s.storeId}>{s.storeName}{s.isActive ? '' : ' (Inactive)'}</option>)}
          </select>
          {selectedStore && <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm"><div className="font-medium">{selectedStore.storeName}</div>{selectedStore.location && <div className="text-gray-600">{selectedStore.location}</div>}</div>}
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><Package className="h-5 w-5 mr-2 text-green-600" />2. Select Product</h3>
          <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} disabled={!selectedStoreId} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Choose product...</option>
            {products.map(p => <option key={p.productId} value={p.productId}>{p.name} ({p.sku})</option>)}
          </select>
          {selectedProduct && <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm"><div className="font-medium">{selectedProduct.name}</div><div className="text-gray-600">SKU: {selectedProduct.sku}</div></div>}
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><Calendar className="h-5 w-5 mr-2 text-purple-600" />3. Select Batch</h3>
          <select value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)} disabled={!selectedProductId} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Choose batch...</option>
            {displayBatches.map(b => <option key={b.batchId} value={b.batchId}>{b.name}{b.assignedStoreId ? ' (Assigned)' : ' (Unassigned)'}</option>)}
          </select>
          {selectedBatch && <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm"><div className="font-medium">{selectedBatch.name}</div>{selectedBatch.batchNumber && <div className="text-gray-600">#{selectedBatch.batchNumber}</div>}{!selectedBatch.assignedStoreId && <div className="text-orange-600 text-xs mt-1">Warning: Batch not assigned to store</div>}</div>}
        </div>
      </div>

      {selectedStoreId && selectedProductId && selectedBatchId && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center"><Scan className="h-6 w-6 mr-2 text-blue-600" />Tag Activation</h3>
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <p className="text-gray-600">Activate tags with full GEO validation, CSV import, and batch linking.</p>
            <button onClick={() => { window.location.href = `/business/activate-tags?businessId=${businessId}&productId=${selectedProductId}&batchId=${selectedBatchId}&storeId=${selectedStoreId}`; }}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
              <QrCode size={18} />Go to Activation Page
            </button>
            <p className="text-sm text-gray-500">Your GPS location will be verified before activation.</p>
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center"><QrCode className="h-5 w-5 mr-2 text-purple-600" />Tags ({filteredTags.length} of {tags.length})</h3>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" /><input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tag ID..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
              <div className="relative"><Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" /><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"><option value="all">All Status</option>{uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              {(searchQuery || statusFilter !== 'all') && <button onClick={() => { setSearchQuery(""); setStatusFilter("all"); }} className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800"><X className="h-4 w-4 mr-1" />Clear</button>}
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto"><div className="space-y-2">
            {filteredTags.map(tag => (
              <div key={tag.tagId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3">{getStatusIcon(tag.status)}<div><div className="font-mono text-sm font-medium">{tag.tagId}</div>{tag.activation?.activatedAt && <div className="text-xs text-gray-500">Activated: {new Date(tag.activation.activatedAt).toLocaleDateString()}</div>}</div></div>
                <div className="flex items-center space-x-2">
                  {tag.stats?.totalScans !== undefined && <span className="text-xs text-gray-500">{tag.stats.totalScans} scans</span>}
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(tag.status)}`}>{tag.status}</span>
                  <button onClick={() => loadTagDetails(tag.tagId)} className="p-1 text-blue-600 hover:text-blue-800" title="View details"><Eye className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {filteredTags.length === 0 && <div className="text-center py-8 text-gray-500">No tags match your search criteria</div>}
          </div></div>
        </div>
      )}

      {/* Tag Details Modal */}
      {showTagDetails && selectedTag && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6"><h3 className="text-2xl font-bold text-gray-900">Tag Details</h3><button onClick={() => setShowTagDetails(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button></div>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium text-gray-600">Tag ID</label><div className="font-mono text-lg font-semibold">{selectedTag.tagId}</div></div>
                <div><label className="text-sm font-medium text-gray-600">Status</label><div><span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedTag.status)}`}>{selectedTag.status}</span></div></div>
              </div>
              {selectedTag.activation && <div className="border-t pt-4"><h4 className="font-semibold text-gray-900 mb-3">Activation Information</h4><div className="grid grid-cols-2 gap-4 text-sm"><div><label className="text-gray-600">Activated At</label><div>{selectedTag.activation.activatedAt ? new Date(selectedTag.activation.activatedAt).toLocaleString() : 'N/A'}</div></div><div><label className="text-gray-600">Method</label><div>{selectedTag.activation.method || 'N/A'}</div></div></div></div>}
              {selectedTag.stats && <div className="border-t pt-4"><h4 className="font-semibold text-gray-900 mb-3">Statistics</h4><div className="grid grid-cols-2 gap-4 text-sm"><div><label className="text-gray-600">Total Scans</label><div className="text-2xl font-bold text-blue-600">{selectedTag.stats.totalScans || 0}</div></div>{selectedTag.stats.lastScannedAt && <div><label className="text-gray-600">Last Scanned</label><div>{new Date(selectedTag.stats.lastScannedAt).toLocaleString()}</div></div>}</div></div>}
              {selectedTag.statusHistory && selectedTag.statusHistory.length > 0 && (
                <div className="border-t pt-4"><h4 className="font-semibold text-gray-900 mb-3 flex items-center"><History className="h-5 w-5 mr-2 text-blue-600" />Status History</h4><div className="space-y-2">{selectedTag.statusHistory.map((h, i) => (<div key={i} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"><div><div className="font-medium">{h.status}</div>{h.reason && <div className="text-sm text-gray-600">{h.reason}</div>}{h.changedBy && <div className="text-xs text-gray-500">By: {h.changedBy}</div>}</div><div className="text-sm text-gray-500">{new Date(h.timestamp).toLocaleString()}</div></div>))}</div></div>
              )}
              <div className="border-t pt-4"><h4 className="font-semibold text-gray-900 mb-3">Actions</h4>
                {selectedTag.status === 'blocked' ? (
                  <button onClick={() => handleUnblockTag(selectedTag.tagId)} disabled={operationLoading} className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"><Unlock className="h-4 w-4 mr-2" />Unblock Tag</button>
                ) : (
                  <div className="space-y-3">
                    <input type="text" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Reason for blocking (optional)" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
                    <button onClick={() => handleBlockTag(selectedTag.tagId)} disabled={operationLoading} className="w-full flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"><Lock className="h-4 w-4 mr-2" />Block Tag</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && stats && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6"><h3 className="text-2xl font-bold text-gray-900">Activation Statistics</h3><button onClick={() => setShowStats(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button></div>
            {stats.success && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg"><div className="text-2xl font-bold text-blue-600">{stats.stats?.totalActivated || 0}</div><div className="text-sm text-gray-600">Total Activated</div></div>
                  <div className="text-center p-4 bg-green-50 rounded-lg"><div className="text-2xl font-bold text-green-600">{stats.stats?.totalScanned || 0}</div><div className="text-sm text-gray-600">Total Scanned</div></div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg"><div className="text-2xl font-bold text-purple-600">{stats.stats?.scanRate || '0%'}</div><div className="text-sm text-gray-600">Scan Rate</div></div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg"><div className="text-2xl font-bold text-orange-600">{stats.activationMethods?.length || 0}</div><div className="text-sm text-gray-600">Methods Used</div></div>
                </div>
                {stats.recentActivations?.length > 0 && (
                  <div><h4 className="font-semibold text-gray-900 mb-3">Recent Activations</h4><div className="space-y-2">{stats.recentActivations.slice(0, 5).map((a: any, i: number) => (<div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded"><span className="font-mono text-sm">{a.tagId}</span><span className="text-xs text-gray-500">{new Date(a.activatedAt).toLocaleString()}</span></div>))}</div></div>
                )}
              </div>
            )}
            <div className="flex justify-end mt-6"><button onClick={() => setShowStats(false)} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsManager;